import { Button, Input, FormGroup, Label, Select } from '@/components'
import { FlexWrapper } from '@/components/layout/flex/flexWrapper'
import { useAxios } from '@/context/axiosContext'
import { useEffect, useState } from 'react'
import DataTable, { TableColumn } from 'react-data-table-component'
import { useLocation, useNavigate } from 'react-router-dom'
import commonUtils from '@/utils/commonUtils'
import { tableStyles } from '@/styles/dataTable/style'
import { useRedis } from '@/hooks/useRedis'

type TInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

interface DataRow {
  rnum: number
  cmpnyCd: string
  termsId: string
  termsVer: string
  termsTit: string
  termsCont: string
  termsGubn: string
  useYn: string
  effectiveDate: string
  regId: string
  regDate: string
  updId: string
  updDate: string
}

const termsMgmt: React.FC = () => {
  const axios = useAxios()
  const navigate = useNavigate()
  const location = useLocation()
  const [data, setData] = useState<DataRow[]>([])
  const { getComCodeValue } = useRedis()

  const [termsTit, setTermsTit] = useState(location.state?.termsTit || '')
  const [srchData, setSrchData] = useState({
    termsTit: '',
    useYn: '',
    termsGubn: '',
  })

  const [currentPage, setCurrentPage] = useState(location.state?.page || 1)
  const [perPage, setPerPage] = useState(location.state?.perPage || 10)

  const columns: TableColumn<DataRow>[] = [
    {
      name: 'No.',
      selector: (row) => row.rnum,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div style={{ width: '100%', textAlign: 'right' }}>{row.rnum}</div>
      ),
    },
    {
      name: '약관ID',
      selector: (row) => row.termsId,
      sortable: true,
      center: true,
      wrap: true,
    },
    {
      name: '약관버전',
      selector: (row) => row.termsVer,
      sortable: true,
      center: true,
      wrap: true,
    },
    {
      name: '약관제목',
      selector: (row) => row.termsTit,
      sortable: true,
      wrap: true,
    },
    {
      name: '약관구분',
      selector: (row) =>
        getComCodeValue('TERMS_GUBN', row.termsGubn)?.codeNm || '',
      sortable: true,
      center: true,
      wrap: true,
    },
    {
      name: '사용여부',
      selector: (row) => getComCodeValue('USE_YN', row.useYn)?.codeNm || '',
      sortable: true,
      center: true,
      wrap: true,
    },
    {
      name: '시행일시',
      selector: (row) => row.effectiveDate,
      sortable: true,
      center: true,
      wrap: true,
    },
    {
      name: '수정자',
      selector: (row) => row.updId,
      sortable: true,
      center: true,
      wrap: true,
    },
    {
      name: '수정일',
      selector: (row) => row.updDate,
      format: (row) => commonUtils.formatDate(row.updDate, false),
      sortable: true,
      wrap: true,
      center: true,
    },
    {
      name: '',
      cell: (row) => (
        <>
          <Button
            variant="outlined"
            color="neverbe"
            size="xs"
            onClick={() => handleButtonUpdateClick(row.termsId, row.termsVer)}
          >
            수정
          </Button>
        </>
      ),
      center: true,
      sortable: false,
    },
  ]

  const handleSearch = (e: React.ChangeEvent<TInput>) => {
    const { name, value } = e.target as HTMLInputElement | HTMLTextAreaElement // 타입 캐스팅
    setSrchData((prev) => ({ ...prev, [name]: value }))
  }

  const handleButtonUpdateClick = async (termsId: string, termsVer: string) => {
    // navigate(`/termsMgmt/detail?termsId=${termsId}`)
    navigate(`/termsMgmt/detail`, {
      state: {
        termsId: termsId,
        termsVer: termsVer,
        termsTit: termsTit,
        page: currentPage,
        perPage: perPage,
      },
    })
  }

  const getTermsList = async () => {
    try {
      const params: any = {}
      if (srchData.termsTit.trim()) {
        params.termsTit = srchData.termsTit
      }
      if (srchData.useYn.trim()) {
        params.useYn = srchData.useYn
      }
      if (srchData.termsGubn.trim()) {
        params.termsGubn = srchData.termsGubn
      }

      const response = await axios.get('/termsMgmt/list', { params })

      const listData = response.data.data.termsList

      if (Array.isArray(listData)) {
        setData(listData)
      }
    } catch (error) {
      console.error('Error fetching term list:', error)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      getTermsList()
    }
  }

  useEffect(() => {
    getTermsList()
  }, [])

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          marginBottom: '10px',
          alignItems: 'center',
          marginTop: '50px',
        }}
      >
        <p style={{ marginLeft: '20px', fontSize: '20px' }}>약관관리</p>
        <div style={{ marginLeft: 'auto' }}>
          <div style={{ display: 'flex', gap: '5px' }}>
            <FormGroup>
              <Label htmlFor={'termsGubn'}>약관구분</Label>
              <Select
                id="termsGubn"
                name="termsGubn"
                width="100px"
                onChange={handleSearch}
              >
                <option value="">전체</option>
                <option value="01">필수</option>
                <option value="02">미사용</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor={'useYn'}>사용여부</Label>
              <FlexWrapper gap="10px">
                <Select
                  id="useYn"
                  name="useYn"
                  width="100px"
                  onChange={handleSearch}
                >
                  <option value="">전체</option>
                  <option value="Y">사용</option>
                  <option value="N">미사용</option>
                </Select>
                <Input
                  placeholder="약관제목을 입력하세요"
                  width="300px"
                  id="srchStr"
                  name="srchStr"
                  value={srchData.termsTit}
                  onChange={handleSearch}
                  onKeyDown={handleKeyDown}
                />
              </FlexWrapper>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={''}>
                <></>
              </Label>
              <FlexWrapper gap="10px">
                <Button
                  onClick={getTermsList}
                  color="neverbe"
                  variant="outlined"
                >
                  검색
                </Button>
                <Button
                  onClick={() => {
                    navigate('/termsMgmt/detail', {
                      state: {
                        termsTit: termsTit,
                        page: currentPage,
                        perPage: perPage,
                      },
                    })
                  }}
                  color="neverbe"
                >
                  등록
                </Button>
              </FlexWrapper>
            </FormGroup>
          </div>
        </div>
      </div>
      <DataTable
        // TODO 페이징처리는 서버 vs 클라이언트 (현행 클라이언트)
        columns={columns}
        data={data}
        // customStyles={tableStyles}
        paginationDefaultPage={currentPage}
        onChangePage={(page) => setCurrentPage(page)}
        pagination={true}
        paginationPerPage={perPage}
        onChangeRowsPerPage={(page) => setPerPage(page)}
        paginationRowsPerPageOptions={[10, 20, 30, 50, 100, 500]}
        noDataComponent={<>조회결과가 없습니다.</>}
        customStyles={tableStyles}
      />
    </>
  )
}
export default termsMgmt
